import React, { Dispatch, useContext, useEffect, useState } from 'react';
import { initialSite, SiteContext } from '../../context/SiteContext';
import { Journey, PageAbsolutePath } from '../../consts/Common';
import { Link, useNavigate } from 'react-router-dom';
import { CommonValidation } from './CommonValidation/commonValidation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { faHouse } from '@fortawesome/free-solid-svg-icons/faHouse';
import { faSterlingSign } from '@fortawesome/free-solid-svg-icons/faSterlingSign';
import { faRepeat } from '@fortawesome/free-solid-svg-icons/faRepeat';
import { InsuranceJourneyState } from './CommonTypes';
import ActionTypes from '../../journeyState/InsuranceJourney/ActionTypes';
import useCarInsuranceController from '../../hooks/insuranceJourney/useCarInsuranceController';
import { useCurrentPath } from '../sitePages/useCurrentPath';
import NavigationWarningDialog from './NavigationWarningDialog';
import { setCountryCode } from './HelperMethods';
import { useMediaQuery } from 'react-responsive';
import { getStartQuotePath } from '../sitePages/insurance/InsuranceMethods';

interface props {
  setQuote?: Dispatch<ActionTypes>;
}

const NavBar = React.memo(function NavBar({ setQuote }: props): JSX.Element {
  const currentPath = useCurrentPath();
  const { site, setSite } = useContext(SiteContext);
  const { journeyType } = site;
  const [showDialog, setShowDialog] = useState(false);
  const [path, setPath] = useState('');
  const [continueToPage, setContinueToPage] = useState(false);
  const navigate = useNavigate();
  const { getNewQuote } = useCarInsuranceController();
  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });

  const logoUrl =
    (isMobile
      ? site.content?.common?.navMobileLogoUrl
      : site.content?.common?.navLogoUrl) ?? '';

  useEffect(() => {
    if (continueToPage) {
      setContinueToPage(false);
      navigate(path);
    }
  }, [continueToPage]);

  const getNewQuoteSuccess = (quote: InsuranceJourneyState) => {
    setQuote &&
      setQuote({
        type: 'set-quote',
        quote: {
          ...quote,
          tiaProductCode: site.brand.tiaProductCode
        }
      });

    navigate(
      getStartQuotePath(
        site.user.isDealer ?? false,
        site.brand.useAlternateEligibility
      )
    );
  };

  const getNewQuoteFailure = (error: string) => {
    console.log('quote error - ' + error);
  };

  const onClickStartNewQuote = () => {
    getNewQuote(
      site.brand.brandName,
      site.user?.insuranceDealerCode ?? '',
      site.user.token,
      getNewQuoteSuccess,
      getNewQuoteFailure
    );
  };

  if (journeyType === Journey.Insurance) {
    const showStartQuote = !site.brand.useAlternateEligibility;

    const homeLink = site.brand.homeLink;
    const showMyAccountLink = site.brand.showMyAccount;

    const dealerLoggedIn =
      site.user.isDealer &&
      CommonValidation.isNotEmpty(site.user.insuranceDealerName);

    const isCurrentPathInQuestionnaire =
      currentPath === PageAbsolutePath.Insurance.AboutYou ||
      currentPath === PageAbsolutePath.Insurance.DrivingHistory ||
      currentPath === PageAbsolutePath.Insurance.VehicleDetails ||
      currentPath === PageAbsolutePath.Insurance.AddressDetailsPage ||
      currentPath === PageAbsolutePath.Insurance.AdditionalDriverDetailsPage ||
      currentPath === PageAbsolutePath.Insurance.YourQuote ||
      currentPath === PageAbsolutePath.Insurance.InsuranceCoverDetailsPage ||
      currentPath === PageAbsolutePath.Insurance.ConfirmationPage ||
      currentPath === PageAbsolutePath.Insurance.OptionalCover ||
      currentPath === PageAbsolutePath.Insurance.CheckYourDetails ||
      currentPath ===
        PageAbsolutePath.Insurance.AboutTermsOfBusinessAndFairUsageNotice ||
      currentPath === PageAbsolutePath.Insurance.PaymentPage;

    const isCurrentPathInHomePage =
      currentPath === PageAbsolutePath.Insurance.DealerLandingPage ||
      currentPath === PageAbsolutePath.Insurance.DealersHomePage ||
      currentPath === PageAbsolutePath.Insurance.Landing;

    const onClickLink = (path: string) => {
      const fullPath = setCountryCode(path, site.countryCode);
      setPath(fullPath);
      if (isCurrentPathInQuestionnaire) {
        setShowDialog(true);
      } else {
        setContinueToPage(true);
      }
    };

    const onCloseModal = () => {
      setShowDialog(false);
    };

    if (dealerLoggedIn)
      return (
        <>
          <nav
            className={`navbar-${site.brand.brandName} flex-row nav-bar-container`}>
            <div className='navbar-brand'>
              <img
                src={logoUrl}
                alt='logo'
                onClick={() =>
                  !isCurrentPathInHomePage &&
                  onClickLink(PageAbsolutePath.Insurance.DealersHomePage)
                }
              />
            </div>
            <div className='navbar-links'>
              {!isCurrentPathInHomePage && (
                <Link
                  id='lnkHome'
                  to='#'
                  onClick={() =>
                    onClickLink(PageAbsolutePath.Insurance.DealersHomePage)
                  }>
                  Home
                </Link>
              )}
              <Link
                onClick={() => {
                  setSite({
                    ...site,
                    user: { ...initialSite.user, isDealer: false }
                  });
                }}
                id='lnkDealerLogin'
                to={PageAbsolutePath.Insurance.DealerLandingPage}>
                Logout
              </Link>
            </div>
          </nav>
          <NavigationWarningDialog
            showDialog={showDialog}
            path={path}
            onCloseModal={onCloseModal}
            brandName={site.brand.brandName}
          />
        </>
      );
    else
      return (
        <>
          <nav
            className={`navbar-${site.brand.brandName} flex-row nav-bar-container`}>
            <div className='navbar-brand'>
              <img
                src={logoUrl}
                alt='logo'
                onClick={() =>
                  !isCurrentPathInHomePage &&
                  onClickLink(
                    site.user.isDealer
                      ? PageAbsolutePath.Insurance.DealerLandingPage
                      : PageAbsolutePath.Insurance.Landing
                  )
                }
              />
            </div>
            <div className='navbar-links mobile-hide'>
              {homeLink && (
                <Link id='lnkKiaWebsite' to={homeLink} target='_blank'>
                  Visit the {site.brand.brandDisplayName} website
                </Link>
              )}
              {showStartQuote && (
                <Link id='lnkStartQuote' to='' onClick={onClickStartNewQuote}>
                  Start a new quote
                </Link>
              )}
              <Link
                id='lnkRetrieveQuote'
                to={PageAbsolutePath.Insurance.RetrieveQuote}>
                Retrieve a quote
              </Link>
              <Link id='lnkHelp' to={PageAbsolutePath.Insurance.HelpPage}>
                Help
              </Link>
              {showMyAccountLink && !dealerLoggedIn && (
                <Link
                  id='lnkKiaWebsite'
                  to={PageAbsolutePath.Insurance.MyAccount}>
                  My Account
                </Link>
              )}
              {!isCurrentPathInHomePage && (
                <Link
                  id='lnkHome'
                  to='#'
                  onClick={() =>
                    onClickLink(
                      site.user.isDealer
                        ? PageAbsolutePath.Insurance.DealerLandingPage
                        : PageAbsolutePath.Insurance.Landing
                    )
                  }>
                  Home
                </Link>
              )}
              {currentPath === PageAbsolutePath.Insurance.DealerLandingPage && (
                <Link
                  id='lnkDealerLogin'
                  to={PageAbsolutePath.Insurance.DealerLoginPage}>
                  Dealer login
                </Link>
              )}
            </div>
            <div className='navbar-links desktop-hide'>
              <div className='container-fluid'>
                <button
                  className='navbar-toggler'
                  type='button'
                  data-bs-toggle='collapse'
                  data-bs-target='#navbarToggle'
                  aria-controls='navbarToggle'
                  aria-expanded='false'
                  aria-label='Toggle navigation'>
                  <span className='navbar-toggler-icon'>
                    <FontAwesomeIcon icon={faBars} size='2xl' />
                  </span>
                </button>
                <div className='mobile-menu collapse' id='navbarToggle'>
                  <ul>
                    <li className='flex-row' key='new-quote'>
                      <div className='nav-list-item-icon'>
                        <FontAwesomeIcon icon={faSterlingSign} />
                      </div>
                      <Link
                        id='lnkStartNewQuote'
                        onClick={onClickStartNewQuote}
                        to=''>
                        <div
                          data-bs-toggle='collapse'
                          data-bs-target='#navbarToggle'
                          aria-controls='navbarToggle'
                          aria-expanded='false'
                          aria-label='Toggle navigation'>
                          Start a new quote
                        </div>
                      </Link>
                    </li>
                    <li className='flex-row' key='retrieve-quote'>
                      <div className='nav-list-item-icon'>
                        <FontAwesomeIcon icon={faRepeat} />
                      </div>
                      <Link
                        id='lnkRetrieveQuote'
                        to={PageAbsolutePath.Insurance.RetrieveQuote}>
                        <div
                          data-bs-toggle='collapse'
                          data-bs-target='#navbarToggle'
                          aria-controls='navbarToggle'
                          aria-expanded='false'
                          aria-label='Toggle navigation'>
                          Retrieve a quote
                        </div>
                      </Link>
                    </li>
                    <li className='flex-row' key='help'>
                      <div className='nav-list-item-icon'>
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                      <Link
                        id='lnkHelp'
                        to={PageAbsolutePath.Insurance.FaqsPage}>
                        <div
                          data-bs-toggle='collapse'
                          data-bs-target='#navbarToggle'
                          aria-controls='navbarToggle'
                          aria-expanded='false'
                          aria-label='Toggle navigation'>
                          Help
                        </div>
                      </Link>
                    </li>
                    {site.user.isDealer && (
                      <li className='flex-row' key='dealer-login'>
                        <div className='nav-list-item-icon'>
                          <FontAwesomeIcon icon={faHouse} />
                        </div>
                        <Link
                          id='lnkDealerLogin'
                          to={PageAbsolutePath.Insurance.DealerLoginPage}>
                          Dealer login
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </nav>
          <NavigationWarningDialog
            showDialog={showDialog}
            path={path}
            onCloseModal={onCloseModal}
            brandName={site.brand.brandName}
          />
        </>
      );
  }

  return (
    <nav className='navbar flex-row nav-bar-container'>
      <span className='navbar-brand'>
        <img
          src={logoUrl}
          alt='logo'
          style={{ minHeight: '50px', maxHeight: '100px', width: '100%' }}
        />
      </span>
    </nav>
  );
});

export default NavBar;
